import type { ExpressTool } from '@/api'

/**
 * The optional "Connect more of your tools" step (#1942): its server key, where its screen lives, and the words both
 * setup paths show for it, so the guided shell, the step screen and the setup conversation say the same thing.
 */
export const MORE_TOOLS_STEP = 'more_tools'

/** Where the step's screen lives, and the one path an Express sign-in may come back to besides the Tools page. */
export const MORE_TOOLS_PATH = '/onboarding/more-tools'

export const MORE_TOOLS_TITLE = 'Connect more of your tools (optional)'

/** What Skip does, said on the control itself. */
export const MORE_TOOLS_SKIP = 'Skip for now. You can connect tools later from the Tools page.'

/** The tools a first-time person most often has, shown first and in this order (those the directory holds). */
export const COMMON_TOOLS: readonly string[] = ['github', 'google', 'slack', 'notion', 'linear']

/** Whether a tool is one of the common ones. */
export function isCommonTool(tool: Pick<ExpressTool, 'slug'>): boolean {
    return COMMON_TOOLS.includes(tool.slug)
}

/** The tools with the common ones first, in `COMMON_TOOLS` order, and the rest in the order given. */
export function commonToolsFirst<T extends Pick<ExpressTool, 'slug'>>(tools: readonly T[]): T[] {
    const common = COMMON_TOOLS.flatMap(slug => tools.filter(tool => tool.slug === slug))
    return [...common, ...tools.filter(tool => !isCommonTool(tool))]
}

/** Where the step keeps the tool whose sign-in it started, for this tab only. */
const STARTED_KEY = 'zh.moreTools.started'

/**
 * Remembers the tool whose sign-in the step started, in this tab (#1942), so coming back from a sign-in that did not
 * finish can name it. The callback itself only says whether it finished.
 */
export function rememberStartedTool(name: string): void {
    try {
        window.sessionStorage.setItem(STARTED_KEY, name)
    } catch {
        // Without session storage the sentence names no tool; nothing else depends on it.
    }
}

/** The tool whose sign-in the step started in this tab, once: it is forgotten as it is read. Null when none. */
export function takeStartedTool(): string | null {
    try {
        const name = window.sessionStorage.getItem(STARTED_KEY)
        window.sessionStorage.removeItem(STARTED_KEY)
        return name
    } catch {
        return null
    }
}

/** The one plain sentence for a sign-in that was cancelled, refused or failed, naming the tool when it is known. */
export function signInDidNotFinish(name: string | null): string {
    return name
        ? `Signing in to ${name} didn't finish, so it isn't connected. Try again or pick another tool.`
        : "Signing in didn't finish, so the tool isn't connected. Try again or pick another tool."
}
